// Copyright 2020 Drivetrain Hub LLC
// GraphQL handlers for gear data.

import {formatValue} from "../../assets/js/utils";
import gearFunctions from "../../assets/js/gear";
import requests from "../../requests";
import {getUnitAngle, mutateUnits, UNITS} from "./units";
import helper from "./helper";
import lib from "./lib";

// region GEAR MACRO

/**
 * Create an array for tabulating gear geometry parameter data in an el-table element.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function tableFilterGear(units) {

  function colLabel(paramStr) {
    return helper.formatElTableLabel(lookupGearGeomInfo, paramStr, units)
  }

  // colData props API:  label, prop, formatter, minWidth, align

  return [{
    label: 'Name',
    prop: 'name',
    minWidth: '140',
  }, {
    label: 'Number',
    prop: 'number',
    minWidth: '80',
  }, {
    label: colLabel('numberOfTeeth'),
    prop: 'geom.numberOfTeeth',
    align: 'center',
  }, {
    label: colLabel('moduleNormal'),
    prop: 'geom.moduleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('pressureAngleNormal'),
    prop: 'geom.pressureAngleNormal',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: colLabel('helixAngle'),
    prop: 'geom.helixAngle',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

// region GEOM

const GEAR_GEOM_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify gear params

  // PERSISTED (GearDetail)
  // skipping cutType, cutWorkflow
  internal: ['Internal gear', '', 'none'],
  numberOfTeeth: ['Number of teeth', '$z$', 'none'],
  moduleNormal: ['Normal module', '$m_n$', 'length'],
  pressureAngleNormal: ['Normal pressure angle', '$\\alpha_n$', 'angle'],
  helixAngle: ['Helix angle', '$\\beta$', 'angle'],
  facewidth: ['Facewidth', '$b$', 'length'],
  rootDiameter: ['Root diameter', '$d_f$', 'length'],
  tipDiameter: ['Tip diameter', '$d_a$', 'length'],
  rimDiameter: ['Rim diameter', '$d_\\text{rim}$', 'length'],
  workPieceDiameter: ['Work piece diameter', '$d_\\text{wp}$', 'length'],
  toothThicknessNormal: [['Pre-finished ', 'normal tooth thickness'], '$s_n$', 'length'],  // circular
  toothThicknessNormalFinish: [['Finished ', 'normal tooth thickness'], '$s_n$', 'length'],  // circular
  flankRoughnessRa: ['Flank roughness', '$\\text{Ra}$', 'length'],
  rootRoughnessRa: ['Root roughness', '$\\text{Ra}$', 'length'],

  // OTHERS
  moduleTransverse: ['Transverse module', '$m_t$', 'length'],
  pressureAngleTransverse: ['Transverse pressure angle', '$\\alpha_t$', 'angle'],
  toothThicknessTransverse: ['Transverse Tooth thickness', '$s_t$', 'length'],  // circular
  referenceDiameter: ['Theoretical pitch diameter', '$d_p$', 'length'],
  baseDiameter: ['Base diameter', '$d_b$', 'length'],
  formDiameter: ['Form diameter', '$d_F$', 'length'],
  helixAngleBase: ['Helix angle @ base dia.', '$\\beta_b$', 'angle'],
  pitchBase: ['Base pitch', '$p_b$', 'length'],
  pitch: ['Pitch', '$p$', 'length'],
};
const GEAR_GEOM_KEYS = Object.keys(GEAR_GEOM_INFO);

/**
 * Lookup the description, symbol and units of a gear parameter.
 * @param {string} paramStr - Gear parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Gear parameter info.
 */
function lookupGearGeomInfo(paramStr) {
  return helper.lookupParamInfo(GEAR_GEOM_INFO, GEAR_GEOM_KEYS, paramStr);
}

/**
 * Convert units of gear properties.
 * @param {Object} gear - Gear object, not part.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertGearGeom(gear, fromUnits=UNITS, toUnits=UNITS) {
  let geom = gear.geom;
  mutateUnits(geom, GEAR_GEOM_INFO, fromUnits, toUnits);
  if (geom.cutGeneratingRack) convertGeneratingRack(geom.cutGeneratingRack, fromUnits, toUnits);
  if (geom.cutGeneratingPinion) convertGeneratingPinion(geom.cutGeneratingPinion, fromUnits, toUnits);

  return gear;
}

/**
 * Flip sign of values applicable to internal gear, namely number of teeth and diameters.
 * @param {Object} gear - Gear object, not part.  Function mutates this object.
 * @return {Object} Mutated input object.
 */
function gearGeomInternalFlip(gear) {
  // See GearDetailGeomInput of API schema for props to consider.
  // Values changed to negative: number of teeth, diameters.
  // Values unchanged: module, pressure angle, helix angle, tooth thicknesses.

  let geom = gear.geom;
  geom.numberOfTeeth = -geom.numberOfTeeth;
  geom.rimDiameter = -geom.rimDiameter;
  geom.rootDiameter = -geom.rootDiameter;
  geom.tipDiameter = -geom.tipDiameter;
  geom.workPieceDiameter = -geom.workPieceDiameter;

  return gear;
}

/**
 * Set the sign of values for use with API as internal gear.  Safer to use than gearGeomInternalFlip.
 * @param {Object} gear - Gear object, not part.  Function mutates this object.
 * @return {Object} Mutated input object.
 */
function gearGeomInternalToAPI(gear) {
  // See GearDetailGeomInput of API schema for props to consider.
  // Values changed to negative: number of teeth, diameters.
  // Values unchanged: module, pressure angle, helix angle, tooth thicknesses.

  let geom = gear.geom;
  geom.numberOfTeeth = -Math.abs(geom.numberOfTeeth);
  geom.rimDiameter = -Math.abs(geom.rimDiameter);
  geom.rootDiameter = -Math.abs(geom.rootDiameter);
  geom.tipDiameter = -Math.abs(geom.tipDiameter);
  geom.workPieceDiameter = -Math.abs(geom.workPieceDiameter);

  return gear;
}

/**
 * Create an object for tabulating gear geometry parameter data.
 * @param {Object} gear - Detailed gear object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableGearGeom(gear, units) {
  return lib.libTableParams(gear.geom, lookupGearGeomInfo, units)
}

/**
 * Check if gear is internal, i.e. annulus.
 * @param {Object} geom - Gear geometry object of any type.
 * @return {null|boolean} Boolean to indicate if gear is internal.
 */
function isInternal(geom) {
  switch (geom.__typename) {
    case requests.gear.TYPENAME_GEOM.GEAR_DETAIL:
    case requests.gear.TYPENAME_GEOM.GEAR_PHASING:
      return geom.numberOfTeeth < 0;
    case requests.gear.TYPENAME_GEOM.GEAR_CONCEPT:
      return geom.theoreticalPitchDiameter < 0;
    default:
      return null
  }
}

// endregion

// region RACK CUTTER

export const CUT_GENERATING_RACK_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify generating rack; consider adding '0' subscript to tooling symbols, maybe redundant

  type: ['Type', '', 'none'],
  moduleNormal: GEAR_GEOM_INFO['moduleNormal'],
  pressureAngleNormal: GEAR_GEOM_INFO['pressureAngleNormal'],
  helixAngle: GEAR_GEOM_INFO['helixAngle'],
  profileShiftCoefficient: ['Profile shift coefficient', '$x^*$', 'none'],
  addendumCoefficient: ['Addendum coefficient', '$h_a^*$', 'none'],
  dedendumCoefficient: ['Dedendum coefficient', '$h_f^*$', 'none'],
  tipRadiusCoefficient: ['Tip radius coefficient', '$\\rho_a^*$', 'none'],
  protuberanceAngle: ['Normal protuberance angle', '$\\alpha_{tp}$', 'angle'],  // symbol?
  protuberanceSize: ['Normal protuberance size', '$U_{tp}$', 'length'],  // symbol?

  // tipRadius: ['Tip radius', '$\\rho_a$', 'length'],
  // addendum: ['Addendum', '$h_a$', 'length'],
  // dedendum: ['Dedendum', '$h_f$', 'length'],
  // profileShift: ['Profile shift', '$x$', 'length'],
};
const CUT_GENERATING_RACK_KEYS = Object.keys(CUT_GENERATING_RACK_INFO);

/**
 * Lookup the description, symbol and units of a generating rack parameter.
 * @param {string} paramStr - Generating rack parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Generating rack parameter info.
 */
function lookupCutGeneratingRackInfo(paramStr) {
  return helper.lookupParamInfo(CUT_GENERATING_RACK_INFO, CUT_GENERATING_RACK_KEYS, paramStr);
}

function convertGeneratingRack(cutGeneratingRack, fromUnits=UNITS, toUnits=UNITS) {
  return mutateUnits(cutGeneratingRack, CUT_GENERATING_RACK_INFO, fromUnits, toUnits);
}

/**
 * Create an object for tabulating gear generating rack parameter data.
 * @param {Object} generatingRack - Generating rack object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableGeneratingRack(generatingRack, units) {
  return lib.libTableParams({type: 'Gen. rack', ...generatingRack}, lookupCutGeneratingRackInfo, units);
}

// endregion

// region PINION CUTTER

export const CUT_GENERATING_PINION_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // PLACEHOLDER {symbols} verify generating pinion; consider adding '0' subscript to tooling symbols, maybe redundant

  type: ['Type', '', 'none'],
  numberOfTeeth: ['Number of teeth', '$z_0$', 'none'],
  moduleNormal: GEAR_GEOM_INFO['moduleNormal'],
  pressureAngleNormal: GEAR_GEOM_INFO['pressureAngleNormal'],
  helixAngle: ['Helix angle', '$\\beta_0$', 'angle'],
  addendumCoefficient: CUT_GENERATING_RACK_INFO['addendumCoefficient'],
  dedendumCoefficient: CUT_GENERATING_RACK_INFO['dedendumCoefficient'],
  protuberanceSize: CUT_GENERATING_RACK_INFO['protuberanceSize'],
  tipRadiusCoefficient: CUT_GENERATING_RACK_INFO['tipRadiusCoefficient'],
  profileShiftCoefficient: CUT_GENERATING_RACK_INFO['profileShiftCoefficient'],
  toothThicknessNormal: ['Normal tooth thickness', '$s_n$', 'length'],  // circular
  rootDiameter: ['Root diameter', '$d_{f0}$', 'length'],
  tipDiameter: ['Tip diameter', '$d_{a0}$', 'length'],
  workingPressureAngleTransverse: ['Transverse working pressure angle', '$\\alpha_{tw}$', 'angle'],
  workingCenterDistance: ['Working center distance', '$a_w$', 'length'],
  referenceCenterDistance: ['Reference center distance', '$a_0$', 'length'],
};
const CUT_GENERATING_PINION_KEYS = Object.keys(CUT_GENERATING_PINION_INFO);

/**
 * Lookup the description, symbol and units of a generating pinion parameter.
 * @param {string} paramStr - Generating pinion parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Generating pinion parameter info.
 */
function lookupCutGeneratingPinionInfo(paramStr) {
  return helper.lookupParamInfo(CUT_GENERATING_PINION_INFO, CUT_GENERATING_PINION_KEYS, paramStr);
}

function convertGeneratingPinion(cutGeneratingPinion, fromUnits=UNITS, toUnits=UNITS) {
  return mutateUnits(cutGeneratingPinion, CUT_GENERATING_PINION_INFO, fromUnits, toUnits);
}

function tableGeneratingPinion(generatingPinion, units) {
  return lib.libTableParams({type: 'Gen. pinion', ...generatingPinion}, lookupCutGeneratingPinionInfo, units);
}

// endregion

/**
 * Create an object for tabulating gear cutting tool data.
 * @param {Object} gear - Detailed gear object.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableGearTool(gear, units) {
  const geom = gear.geom;
  const unitAngle = getUnitAngle(units);

  switch (geom.cutType) {
    case requests.gear.CutType.GEN_RACK: {
      // calculate generating tool data
      let toolData = gearFunctions.generatingRackData(geom, geom.cutGeneratingRack.pressureAngleNormal, unitAngle);

      // merge with persisted params; any overwrites should be the same value
      Object.assign(toolData, geom.cutGeneratingRack);

      return tableGeneratingRack(toolData, units);
    }
    case requests.gear.CutType.GEN_PINION: {
      // calculate generating tool data
      const pinionCutter = geom.cutGeneratingPinion;
      let toolData = gearFunctions.generatingPinionData(geom, {
        genPressureAngleNormal: pinionCutter.pressureAngleNormal,
        cutterNumberOfTeeth: pinionCutter.numberOfTeeth,
        cutterProfileShiftCoefficient: pinionCutter.profileShiftCoefficient,
        cutterDedendumCoefficient: pinionCutter.dedendumCoefficient
      }, isInternal(geom), unitAngle);

      // merge with persisted params; any overwrites should be the same value
      Object.assign(toolData, geom.cutGeneratingPinion);

      return tableGeneratingPinion(toolData, units);
    }
  }
}

function gearToolObj(gear, units) {
  // Analogous to tableGearTool, but return the tool object instead of array.

  const geom = gear.geom;
  const unitAngle = getUnitAngle(units);

  switch (geom.cutType) {
    case requests.gear.CutType.GEN_RACK: {
      // calculate generating tool data
      let toolData = gearFunctions.generatingRackData(geom, geom.cutGeneratingRack.pressureAngleNormal, unitAngle);

      // merge with persisted params; any overwrites should be the same value
      Object.assign(toolData, geom.cutGeneratingRack);
      return toolData;
    }
    case requests.gear.CutType.GEN_PINION: {
      // calculate generating tool data
      const pinionCutter = geom.cutGeneratingPinion;
      let toolData = gearFunctions.generatingPinionData(geom, {
        genPressureAngleNormal: pinionCutter.pressureAngleNormal,
        cutterNumberOfTeeth: pinionCutter.numberOfTeeth,
        cutterProfileShiftCoefficient: pinionCutter.profileShiftCoefficient,
        cutterDedendumCoefficient: pinionCutter.dedendumCoefficient
      }, isInternal(geom), unitAngle);

      // merge with persisted params; any overwrites should be the same value
      Object.assign(toolData, geom.cutGeneratingPinion);
      return toolData;
    }
  }
}

// endregion

// region GEAR MICRO

const MICRO_TYPE_LABEL = {
  LINEAR: 'Linear',
  PARABOLIC: 'Parabolic'
};

// region LEAD

const MICRO_LEAD_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // Reference figure A.3 of ISO 1328-1:2013.

  slope: ['Lead slope', '$C_{H\\beta}$', 'lengthSmall'],
  crown: ['Lead crown', '$C_{\\beta}$', 'lengthSmall'],
  evalLimitLower: ['Evaluation limit, bottom', '', 'percentage'],  // not covered by standard
  evalLimitUpper: ['Evaluation limit, top', '', 'percentage'],  // not covered by standard
  bottomRelief: ['Bottom relief, amount', '$C_{\\beta I}$', 'lengthSmall'],
  bottomReliefStart: ['Bottom relief, start', '$L_{CI}$', 'percentage'],
  bottomReliefType: ['Bottom relief, type', '', 'none'],
  topRelief: ['Top relief, amount', '$C_{\\beta II}$', 'lengthSmall'],
  topReliefStart: ['Top relief, start', '$b - L_{CII}$', 'percentage'],
  topReliefType: ['Top relief, type', '', 'none'],
};
const MICRO_LEAD_KEYS = Object.keys(MICRO_LEAD_INFO);

/**
 * Lookup the description, symbol and units of a microgeometry lead parameter.
 * @param {string} paramStr - Microgeometry lead parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Microgeometry lead parameter info.
 */
function lookupMicroLeadInfo(paramStr) {
  return helper.lookupParamInfo(MICRO_LEAD_INFO, MICRO_LEAD_KEYS, paramStr);
}

/**
 * Create arrays of left and right gear microgeometry profile parameter data.
 * @param {Object} gear - Gear model part object, as defined by the Vuex state of model parts.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {[[{parameter: string, symbol: string, value: number || string, units: string}]]} Array of arrays with props required by LibTableParams.vue.
 */
function tablesMicroLead(gear, units) {
  const tableLeadL = lib.libTableParams(gear.micro.leadLeft, lookupMicroLeadInfo, units);
  const tableLeadR = lib.libTableParams(gear.micro.leadRight, lookupMicroLeadInfo, units);

  return [tableLeadL, tableLeadR];
}

// endregion

// region PROFILE

const MICRO_PROFILE_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  // Reference figure A.1 of ISO 1328-1:2013.

  slope: ['Profile slope', '$C_{H\\alpha}$', 'lengthSmall'],
  crown: ['Profile crown', '$C_\\alpha$', 'lengthSmall'],
  evalLimitLower: ['Evaluation limit, root', '', 'percentage'],  // not covered by standard
  evalLimitUpper: ['Evaluation limit, tip', '', 'percentage'],  // not covered by standard
  rootRelief: ['Root relief, amount', '$C_{\\alpha f}$', 'lengthSmall'],
  rootReliefStart: ['Root relief, start', '$L_{C\\alpha f}$', 'percentage'],
  rootReliefType: ['Root relief, type', '', 'none'],
  tipRelief: ['Tip relief, amount', '$C_{\\alpha a}$', 'lengthSmall'],
  tipReliefStart: ['Tip relief, start', '$d_a - L_{C\\alpha a}$', 'percentage'],
  tipReliefType: ['Tip relief, type', '', 'none'],
};
const MICRO_PROFILE_KEYS = Object.keys(MICRO_PROFILE_INFO);

/**
 * Lookup the description, symbol and units of a microgeometry profile parameter.
 * @param {string} paramStr - Microgeometry profile parameter to lookup.
 * @return {{desc: *, symbol: *, units: *}} Microgeometry profile parameter info.
 */
function lookupMicroProfileInfo(paramStr) {
  return helper.lookupParamInfo(MICRO_PROFILE_INFO, MICRO_PROFILE_KEYS, paramStr);
}

/**
 * Create arrays of left and right gear microgeometry profile parameter data.
 * @param {Object} gear - Gear model part object, as defined by the Vuex state of model parts.
 * @param {Object} units - Optional labeling for units.  Units are *not* be converted by this function.
 * @return {[[{parameter: string, symbol: string, value: number || string, units: string}]]} Array of arrays with props required by LibTableParams.vue.
 */
function tablesMicroProfile(gear, units) {
  const tableProfileL = lib.libTableParams(gear.micro.profileLeft, lookupMicroProfileInfo, units);
  const tableProfileR = lib.libTableParams(gear.micro.profileRight, lookupMicroProfileInfo, units);

  return [tableProfileL, tableProfileR];
}

// endregion

/**
 * Convert units of gear microgeometry properties.
 * @param {Object} gear - Gear object.  Function mutates this object.
 * @param {Object} fromUnits - Units to convert from.  Defaults to unit system default.
 * @param {Object} toUnits - Units to convert to.  Defaults to unit system default.
 * @return {Object} Mutated input object.
 */
function convertGearMicro(gear, fromUnits, toUnits) {
  if (gear.micro) {
    // convert lead parameters
    mutateUnits(gear.micro.leadLeft, MICRO_LEAD_INFO, fromUnits, toUnits);
    mutateUnits(gear.micro.leadRight, MICRO_LEAD_INFO, fromUnits, toUnits);

    // convert profile parameters
    mutateUnits(gear.micro.profileLeft, MICRO_PROFILE_INFO, fromUnits, toUnits);
    mutateUnits(gear.micro.profileRight, MICRO_PROFILE_INFO, fromUnits, toUnits);
  }

  return gear;
}

// endregion

// region GEAR MATERIAL

const MATERIAL_LABEL = {
  agma2001D04: "ANSI/AGMA 2001-D04",
  iso633652003: "ISO 6336-5:2003",
};

// region AGMA 2001-D04

/**
 * Create an array for tabulating material data per AGMA 2001-D04 in an el-table element.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function tableFilterAgma2001D04() {
  return [{
    label: 'Designation',
    prop: 'designation',
    minWidth: '72',
  }, {
    label: 'Treatment',
    prop: 'heatTreatment',
    minWidth: '72',
  }, {
    label: 'Grade',
    prop: 'grade',
    minWidth: '30',
    align: 'center',
  }, {
    label: 'Allowable contact (MPa)',
    prop: 'allowableStressContactMin',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: 'Allowable bending (MPa)',
    prop: 'allowableStressBendingMin',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

const AGMA2001_D04_INFO = {
  // PLACEHOLDER {symbols} AGMA
  // <param prop>: [<description>, <symbol>, <unit type>]
  designation: ['Designation','', 'none'],
  heatTreatment: ['Heat treatment', '', 'none'],
  grade: ['Grade', '', 'none'],
  surfaceHardnessMin: ['Surface hardness, min', '', 'surfaceHardnessScale'],
  coreHardnessMin: ['Core hardness, min', '', 'coreHardnessScale'],
  coreHardnessNumber: ['Core hardness, specified', '', 'coreHardnessScale'],  // persisted
  allowableStressContactMin: ['Allowable contact stress, min', '$\\sigma_\\text{Hlim}$', 'pressure'],
  allowableStressBendingMin: ['Allowable bending stress, min', '$\\sigma_\\text{Flim}$', 'pressure'],
};
const AGMA2001_D04_KEYS = Object.keys(AGMA2001_D04_INFO);

function lookupAgma2001D04(paramStr) {
  return helper.lookupParamInfo(AGMA2001_D04_INFO, AGMA2001_D04_KEYS, paramStr);
}

function tableAgma2001D04(mat) {
  // Analogous to handlers.lib.libTableParams, but specifically for handling AGMA 2001-D04 materials.

  // handle null or undefined
  if (!mat) return [];

  let params = {};
  for (const [key, value] of Object.entries(mat)) {
    try {
      // get material param info
      let {info, index} = lookupAgma2001D04(key);

      // specify units string
      let units;
      switch (info.units) {
        case 'surfaceHardnessScale':
          units = mat.surfaceHardnessScale;
          break;
        case 'coreHardnessScale':
          units = mat.coreHardnessScale;
          break;
        default:
          units = UNITS[info.units];
      }

      params[index] = {
        parameter: info.desc,
        symbol: info.symbol,
        value: formatValue(value),
        units: units,
      };
    } catch (e) {
      // do nothing - error probably caused by param not existing
      // console.warn('key: ' + key, e);
    }
  }

  // map params obj to arr, ordered per indexed keys
  return Object.values(params);
}

// endregion

// region ISO 6336-5:2003

/**
 * Create an array for tabulating ISO material data in an el-table element.
 * @return {*[]} Array of objects containing props required by LibTableFilter.vue.
 */
function tableFilterIso633652003() {
  return [{
    label: 'Type',
    prop: 'type',
    minWidth: '100',
  }, {
    label: 'Quality',
    prop: 'quality',
    minWidth: '40',
  }, {
    label: 'Allowable contact (MPa)',
    prop: 'allowableStressContactMin',
    formatter: helper.formatElTableValue,
    align: 'center',
  }, {
    label: 'Nominal bending (MPa)',
    prop: 'nominalStressBendingMin',
    formatter: helper.formatElTableValue,
    align: 'center',
  }];
}

const ISO6336_5_2003_INFO = {
  // PLACEHOLDER {symbols} ISO
  // <param prop>: [<description>, <symbol>, <unit type>]
  type: ['Type', '', 'none'],
  quality: ['Quality', '', 'none'],
  abbreviation: ['Abbreviation', '', 'none'],
  surfaceHardnessMin: ['Surface hardness, min', '', 'surfaceHardnessScale'],
  surfaceHardnessMax: ['Surface hardness, max', '', 'surfaceHardnessScale'],
  surfaceHardnessNumber: ['Surface hardness, specified', '', 'surfaceHardnessScale'],  // persisted
  coreHardnessMin: ['Core hardness, min', '', 'coreHardnessScale'],
  shotPeen: ['Shot peen', '', 'none'],  // persisted
  allowableStressContactMin: ['Allowable contact stress, min', '', 'pressure'],
  nominalStressBendingMin: ['Nominal bending stress, min', '', 'pressure'],
  aContact: ['Contact constant, A', '$a_H$', 'none'],  // units?
  bContact: ['Contact constant, B', '$b_H$', 'none'],  // units?
  aBending: ['Bending constant, A', '$a_F$', 'none'],  // units?
  bBending: ['Bending constant, B', '$b_F$', 'none'],  // units?
};
const ISO6336_5_2003_KEYS = Object.keys(ISO6336_5_2003_INFO);

function lookupIso633652003(paramStr) {
  return helper.lookupParamInfo(ISO6336_5_2003_INFO, ISO6336_5_2003_KEYS, paramStr);
}

function tableIso633652003(mat) {
  // Analogous to handlers.lib.libTableParams, but specifically for handling ISO 6336 materials.

  // handle null or undefined
  if (!mat) return [];

  let params = {};
  for (const [key, value] of Object.entries(mat)) {
    try {
      // get material param info
      let {info, index} = lookupIso633652003(key);

      // specify units string - does not convert units
      let units;
      switch (info.units) {
        case 'surfaceHardnessScale':
          units = mat.surfaceHardnessScale;
          break;
        case 'coreHardnessScale':
          units = mat.coreHardnessScale;
          break;
        default:
          units = UNITS[info.units];
      }

      params[index] = {
        parameter: info.desc,
        symbol: info.symbol,
        value: formatValue(value),
        units: units,
      };
    } catch (e) {
      // do nothing - error probably caused by param not existing
      // console.warn('key: ' + key, e);
    }
  }

  // map params obj to arr, ordered per indexed keys
  return Object.values(params);
}

// endregion

// endregion

// region GEAR QUALITY

const QUALITY_LABEL = {
  agma13281B14: "ANSI/AGMA ISO 1328-1-B14",
  agma20152B15: "ANSI/AGMA 2015-2-B15",
  iso132812013: "ISO 1328-1:2013",
  iso132822020: "ISO 1328-2:2020",
};

// region AGMA 1328-1-B14

const AGMA1328_1_B14_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  toleranceClass: ['Tolerance class', '$A$', 'none'],
  sectorPitches: ['Number of pitches in sector', '$k$', 'none'],
  singlePitchTolerance: ['Single pitch tolerance', '$f_\\rm{pT}$', 'lengthSmall'],
  cumulativePitchTolerance: ['Cumulative pitch tolerance', '$F_\\rm{pT}$', 'lengthSmall'],
  sectorPitchTolerance: ['Sector pitch tolerance', '$F_\\rm{pkT}$', 'lengthSmall'],
  adjacentPitchDifferenceTolerance: ['Adjacent pitch difference tolerance', '$f_\\rm{uT}$', 'lengthSmall'],
  profileSlopeTolerance: ['Profile slope tolerance, +/-', '$f_\\rm{H \\alpha T}$', 'lengthSmall'],
  profileFormTolerance: ['Profile form tolerance', '$f_\\rm{f \\alpha T}$', 'lengthSmall'],
  totalProfileTolerance: ['Total profile tolerance', '$F_\\rm{\\alpha T}$', 'lengthSmall'],
  helixSlopeTolerance: ['Helix slope tolerance, +/-', '$f_\\rm{H \\beta T}$', 'lengthSmall'],
  helixFormTolerance: ['Helix form tolerance', '$f_\\rm{f \\beta T}$', 'lengthSmall'],
  totalHelixTolerance: ['Total helix tolerance', '$F_\\rm{\\beta T}$', 'lengthSmall'],
  runoutTolerance: ['Runout tolerance', '$F_\\rm{rT}$', 'lengthSmall'],
};
const AGMA1328_1_B14_KEYS = Object.keys(AGMA1328_1_B14_INFO);

function lookupAgma13281B14(paramStr) {
  return helper.lookupParamInfo(AGMA1328_1_B14_INFO, AGMA1328_1_B14_KEYS, paramStr);
}

/**
 * Create an array for tabulating AGMA 1328-1-B14 tolerance data.
 * @param {Object} qualityData - Computed quality data per API query.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableAgma13281B14(qualityData) {
  if (!qualityData) return [];
  return lib.libTableParams(qualityData, lookupAgma13281B14)
}

// endregion

// region AGMA 2015-2-B15

const AGMA2015_2_B15_INFO = {
  // <param prop>: [<description>, <symbol>, <unit type>]
  toleranceClass: ['Tolerance class', '$R$', 'none'],
  sectorPitches: ['Number of pitches', '$k$', 'none'],
  radialCompositeToleranceTooth2tooth: ['Tooth-to-tooth radial composite tolerance', '$f_\\rm{idT}$', 'lengthSmall'],
  radialCompositeToleranceTotal: ['Total radial composite tolerance', '$F_\\rm{idT}$', 'lengthSmall'],
  radialCompositeToleranceK: ['Total radial composite tolerance for k pitches', '$F_{\\text{id} k \\text{T}}$', 'lengthSmall'],
};
const AGMA2015_2_B15_KEYS = Object.keys(AGMA2015_2_B15_INFO);

function lookupAgma20152B15(paramStr) {
  return helper.lookupParamInfo(AGMA2015_2_B15_INFO, AGMA2015_2_B15_KEYS, paramStr);
}

/**
 * Create an array for tabulating AGMA 2015-2-B15 tolerance data.
 * @param {Object} qualityData - Computed quality data per API query.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableAgma20152B15(qualityData) {
  if (!qualityData) return [];
  return lib.libTableParams(qualityData, lookupAgma20152B15)
}

// endregion

// region ISO 1328-1:2013
// Standard identical to AGMA 1328-1-B14.

/**
 * Create an array for tabulating ISO 1328-1:2013 tolerance data.
 * @param {Object} qualityData - Computed quality data per API query.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableIso132812013(qualityData) {
  if (!qualityData) return [];
  return tableAgma13281B14(qualityData)
}

// endregion

// region ISO 1328-2:2020
// Parameters same as AGMA 1328-1-B14.

/**
 * Create an array for tabulating ISO 1328-2:2020 tolerance data.
 * @param {Object} qualityData - Computed quality data per API query.
 * @return {{parameter: string, symbol: string, value: number || string, units: string}[]} Array with props required by LibTableParams.vue.
 */
function tableIso132822020(qualityData) {
  if (!qualityData) return [];
  return tableAgma20152B15(qualityData)
}

// endregion

// endregion

// region GEARS

function gearRatio(gear1, gear2) {
  // Rotational ratio, equal to -omega1 / omega2.
  return gear2.geom.numberOfTeeth / gear1.geom.numberOfTeeth;
}

function internalMultiplier(gear1, gear2) {
  // Returns -1 if either gear is internal, otherwise 1.

  const internal1 = isInternal(gear1.geom);
  const internal2 = isInternal(gear2.geom);
  const internalGearset = [internal1, internal2].some(Boolean);

  return internalGearset ? -1 : 1;
}

// endregion

export default {
  // GEOM
  tableFilterGear: tableFilterGear,
  convertGearGeom: convertGearGeom,
  gearGeomInternalFlip: gearGeomInternalFlip,
  gearGeomInternalToAPI: gearGeomInternalToAPI,
  tableGearGeom: tableGearGeom,
  tableGeneratingRack: tableGeneratingRack,
  tableGeneratingPinion: tableGeneratingPinion,
  tableGearTool: tableGearTool,
  gearToolObj: gearToolObj,
  isInternal: isInternal,
  // MICRO
  MICRO_TYPE_LABEL: MICRO_TYPE_LABEL,
  tablesMicroLead: tablesMicroLead,
  tablesMicroProfile: tablesMicroProfile,
  convertGearMicro: convertGearMicro,
  // MATERIAL
  MATERIAL_LABEL: MATERIAL_LABEL,
  tableFilterAgma2001D04: tableFilterAgma2001D04,
  tableAgma2001D04: tableAgma2001D04,
  tableFilterIso633652003: tableFilterIso633652003,
  tableIso633652003: tableIso633652003,
  // QUALITY
  QUALITY_LABEL: QUALITY_LABEL,
  tableAgma13281B14: tableAgma13281B14,
  tableAgma20152B15: tableAgma20152B15,
  tableIso132812013: tableIso132812013,
  tableIso132822020: tableIso132822020,
  // GEARS
  gearRatio: gearRatio,
  internalMultiplier: internalMultiplier,
}
